import React from 'react';
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationPrevious, PaginationNext, PaginationEllipsis,
} from './ui/pagination';

// 1 … 4 5 [6] 7 8 … 20 — always includes the first and last page and the current page's neighbours.
function pageWindow(page, last) {
  const pages = new Set([1, last, page - 1, page, page + 1]);
  if (page <= 3) { pages.add(2); pages.add(3); pages.add(4); }
  if (page >= last - 2) { pages.add(last - 1); pages.add(last - 2); pages.add(last - 3); }
  const sorted = [...pages].filter(p => p >= 1 && p <= last).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('gap-' + p);
    out.push(p);
  });
  return out;
}

/**
 * Shared pager.
 *  - Pass `total` when the API reports it: shows numbered pages and a "Showing a–b of n" caption.
 *  - Otherwise pass `hasMore` (fetch pageSize + 1 rows to know): shows Previous / Next only.
 * `page` is 1-based. Scrolls to the top when the page changes.
 */
export default function PaginationBar({ page, pageSize, total, hasMore, onPageChange, className = '' }) {
  const known = typeof total === 'number';
  const last = known ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const canPrev = page > 1;
  const canNext = known ? page < last : !!hasMore;

  if (!canPrev && !canNext) return null;

  const go = (p) => (e) => {
    e.preventDefault();
    if (p < 1 || (known && p > last) || p === page) return;
    if (p > page && !known && !hasMore) return;
    onPageChange(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const disabled = (off) => (off ? 'pointer-events-none opacity-50' : 'cursor-pointer');

  return (
    <div className={`mt-8 space-y-3 ${className}`} data-testid="pagination">
      {known && (
        <p className="text-center text-sm text-muted-foreground">
          Showing {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
        </p>
      )}
      <Pagination>
        <PaginationContent className="flex-wrap justify-center">
          <PaginationItem>
            <PaginationPrevious href="#" onClick={go(page - 1)} aria-disabled={!canPrev} className={disabled(!canPrev)} />
          </PaginationItem>
          {known ? pageWindow(page, last).map(p => (
            <PaginationItem key={p}>
              {typeof p === 'string'
                ? <PaginationEllipsis />
                : <PaginationLink href="#" isActive={p === page} onClick={go(p)} className="cursor-pointer">{p}</PaginationLink>}
            </PaginationItem>
          )) : (
            <PaginationItem>
              <span className="px-3 text-sm text-muted-foreground">Page {page}</span>
            </PaginationItem>
          )}
          <PaginationItem>
            <PaginationNext href="#" onClick={go(page + 1)} aria-disabled={!canNext} className={disabled(!canNext)} />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
